import { BASE_URL } from "../config/apiConfig";

/** Turn DB/API `images` (array, JSON string, or single path) into a list of path strings. */
export function coerceProductImagePaths(images: unknown): string[] {
  if (images == null) return [];
  if (Array.isArray(images)) {
    return images.filter((x): x is string => typeof x === "string" && x.trim().length > 0).map((x) => x.trim());
  }
  if (typeof images === "string") {
    const trimmed = images.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed.filter((x): x is string => typeof x === "string" && x.trim().length > 0).map((x) => x.trim());
        }
        if (typeof parsed === "string" && parsed.trim()) return [parsed.trim()];
      } catch {
        /* fall through: treat as literal path */
      }
    }
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/") ||
      trimmed.includes("/")
    ) {
      return [trimmed];
    }
  }
  return [];
}

/** Prefer `image`, then first entry in `images` (handles robotics JSON string). */
export function getFirstProductImagePath(
  product: { image?: string | null; images?: unknown } | null | undefined
): string | undefined {
  if (!product) return undefined;
  const direct = typeof product.image === "string" ? product.image.trim() : "";
  if (
    direct &&
    (direct.startsWith("http://") ||
      direct.startsWith("https://") ||
      direct.startsWith("/") ||
      direct.includes("/"))
  ) {
    return direct;
  }
  const fromList = coerceProductImagePaths(product.images);
  return fromList[0];
}

/**
 * Build an absolute URL for product media served from the Online Store API
 * (`/uploads/...`, `/images/Robotics/...`, etc.). `/Uploads/` is normalized to `/uploads/`.
 */
export function normalizeImageUrl(path: string | undefined | null): string {
  if (!path) return "/default-image.png";

  const trimmed = path.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }

  const normalizedPath = trimmed.replace(/\/Uploads\//gi, "/uploads/");

  if (!normalizedPath.startsWith("/")) {
    return `${BASE_URL}/${normalizedPath}`;
  }
  return `${BASE_URL}${normalizedPath}`;
}

/** Resolve cart/listing/detail product-like object to a single absolute image URL. */
export function normalizeProductImageUrl(
  product: { image?: string | null; images?: unknown } | null | undefined
): string {
  return normalizeImageUrl(getFirstProductImagePath(product));
}
